import React from 'react';
import { AddToShoppingCartSource, ISearchProduct } from '@monofront/types';
import { useGlobalSearchContext } from '../../../../../context/GlobalSearchContext';
import { getProductCategory } from '@monofront/business/utils';
import { trackEvent } from '@monofront/tracking';
import {
  HorizontalProductCard,
  HorizontalProductComeBackSoon,
  HorizontalProductImage,
  HorizontalProductPrice,
  HorizontalProductTitle,
  ProductBundle,
  ProductFormat,
  ProductFormatAndUnitPrice,
  ProductOutOfStock,
  ProductPfand,
  ProductPromotionalOffer,
  ProductVendor,
} from '@monofront/design-system/components/ProductCard';
import useProductToShoppingCart from '@hooks/cart/useProductToShoppingCart';
import { useCardProductInfos } from '@hooks/businessContainer';
import { useAuthentication } from '@context/businessContainer';
import { useNotifyMe } from '@monofront/business/hooks';
import { toast } from 'react-toastify';
import { useTranslations } from 'next-intl';
import { HorizontalSearchProductAddToCart } from '@monofront/design-system/components/ProductCard/components/ProductAddToCart';
import { HorizontalSearchProductBadges } from '@monofront/design-system/components/ProductCard/components/ProductBadges';
interface IListPanelProductProps {
  product: ISearchProduct;
}

const ListPanelProduct = ({ product }: IListPanelProductProps) => {
  const { setIsGlobalSearchResultsPanelOpen } = useGlobalSearchContext();

  const onCardProductClick = () => {
    trackEvent({
      type: 'LFSearchRecommendationClick',
      data: {
        recommendationType: 'product',
        itemId: product.sku,
        itemName: product.title,
        itemBrand: product.vendor,
        itemCategory: getProductCategory(product) ?? 'NO_CATEGORY',
        isSearchEmpty: true,
      },
    });
    setIsGlobalSearchResultsPanelOpen(false);
  };

  const {
    localProductQuantity,
    currentProductQuantity,
    incrementCount,
    decrementCount,
  } = useProductToShoppingCart(product, {
    source: AddToShoppingCartSource.SEARCH_INPUT,
  });

  const tCatalog = useTranslations('Catalog');
  const { isAntiWaste } = useCardProductInfos(product);
  const { authUser } = useAuthentication();

  const { loading, notifyMe } = useNotifyMe();

  const handleNotifyMe = async (
    email,
    handleSuccess,
    handleError,
  ): Promise<void> => {
    await notifyMe({
      email,
      sku: product.sku,
      successCallback: (messageKey) => {
        toast(tCatalog(messageKey));
        handleSuccess?.();
      },
      errorCallback: (errorMessageKey) => {
        toast(tCatalog(errorMessageKey), {
          progressClassName: 'toast-error',
        });
        handleError?.(errorMessageKey);
      },
    });
  };

  const hasPfand = product.meta.finance?.pfand;

  return (
    <div onClick={onCardProductClick}>
      <HorizontalProductCard
        product={product}
        isInCart={currentProductQuantity > 0}
        key={product.objectID}
      >
        <HorizontalProductImage />
        <HorizontalSearchProductBadges />
        <HorizontalProductTitle />
        <ProductVendor />
        <ProductFormatAndUnitPrice quantity={localProductQuantity ?? 0} />
        <ProductBundle />
        <ProductPromotionalOffer quantity={localProductQuantity ?? 0} />
        <ProductFormat />
        <HorizontalProductPrice quantity={localProductQuantity ?? 0} />
        {hasPfand && <ProductPfand />}
        <HorizontalSearchProductAddToCart
          handleAddToCart={incrementCount}
          handleRemoveFromCart={decrementCount}
          quantity={localProductQuantity ?? 0}
        />
        {isAntiWaste ? (
          <ProductOutOfStock />
        ) : (
          <HorizontalProductComeBackSoon
            userEmail={authUser?.email}
            handleNotifyMe={handleNotifyMe}
            isNotifyMeLoading={loading}
          />
        )}
      </HorizontalProductCard>
    </div>
  );
};

export default ListPanelProduct;
